import { Typography } from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Dash } from '@/components/common/HtmlEntities';
import { RequestSummary } from '@/domain/newRequest';
import { COLOR, FONT_SIZE, FONT_WEIGHT, PX_TO_REM } from '@/globalStyles';

import { displayNumberOfDays, periodText } from '../helpers';

type Props = {
    summary?: RequestSummary;
};

const WithRestriction = ({ summary }: Props) => {
    const { t } = useTranslation();

    const {
        noLimits,
        periodType,
        daysUsedAdditional,
        parentWithRestriction,
        limitParent,
        limit,
        daysUsedParent,
        daysUsedChild,
        parentName,
        childName,
        childLeftDays,
        isOverLimit,
    } = summary || {};
    const noLimitText = t('requestDetails_no_limit');
    const showChildLeftDaysData = childLeftDays !== undefined;
    const color = isOverLimit ? COLOR.redWildWatermelon : '';

    return (
        <div>
            <SummaryItem>
                <StyledTypography variant="table_text">{t('request_for_title')}:</StyledTypography>
                <TypographyBold color={color}>
                    {daysUsedAdditional ? displayNumberOfDays(daysUsedAdditional, t) : <Dash />}
                </TypographyBold>
            </SummaryItem>
            <SummaryItem>
                <Typography variant="table_text">{t('restriction_title')}:</Typography>
                <Column>
                    <TypographyBold>
                        {/* A pool eaten up by a correction leaves 0 days, which is the opposite of no limit */}
                        {parentName} ({limitParent !== undefined ? displayNumberOfDays(limitParent, t) : noLimitText}
                        &nbsp;
                        {periodText(parentWithRestriction, t)})
                    </TypographyBold>
                    <TypographyBold>
                        {childName} ({displayNumberOfDays(limit, t)} {periodText(periodType, t)})
                    </TypographyBold>
                </Column>
            </SummaryItem>

            <SummaryItem>
                <StyledTypography variant="table_text">{t('requestDetails_usedLimit_title')}:</StyledTypography>
                <Column>
                    <TypographyBold>
                        {parentName} ({displayNumberOfDays(daysUsedParent, t)})
                    </TypographyBold>
                    <TypographyBold>
                        {childName} ({displayNumberOfDays(daysUsedChild, t)})
                    </TypographyBold>
                </Column>
            </SummaryItem>
            <SummaryItem>
                <StyledTypography variant="table_text">{t('left')}:</StyledTypography>
                <TypographyBold>
                    {showChildLeftDaysData ? displayNumberOfDays(childLeftDays, t) : noLimits ? noLimitText : <Dash />}
                </TypographyBold>
            </SummaryItem>
        </div>
    );
};

const SummaryItem = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    &:not(:last-of-type) {
        margin-bottom: 1rem;
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
`;

const StyledTypography = styled(Typography)`
    line-height: 1rem !important;
`;

const TypographyBold = styled(StyledTypography)`
    font-weight: ${FONT_WEIGHT.medium} !important;
    font-size: ${FONT_SIZE.smedium} !important;
    padding-left: ${PX_TO_REM[30]};
`;

const Memoized = memo(WithRestriction);
const Styled = styled(Memoized)``;

export default Styled;
